import React from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";

// GA4 Measurement ID - must match the id in public/index.html
const GA4_MEASUREMENT_ID = "G-VC7DZTPE7E";

// GA4 silently redacts any user-property value shaped like an email address,
// and a redacted value shows up in reports as "(not set)" - identical to a
// logged-out stranger. Several members have their own email address AS their
// display name, so the "@" has to come out of whatever we send, not just out
// of the email fallback.
function ga4SafeName(displayName, email) {
  const raw = String(displayName || email || "").trim();
  const clean = raw.replace(/@/g, " at ");
  return clean || "member (no name)";
}

function setGA4User(gtag, uid, email, displayName, subscriptionActive, tier) {
  if (typeof gtag !== "function") return;
  if (uid) {
    // gtag("config", ...) a SECOND time for an already-configured measurement
    // id does not apply user_id - the export showed zero events carrying one.
    // gtag("set", ...) is the documented way to attach it after load.
    gtag("set", { user_id: uid });
    gtag("set", "user_properties", {
      email: email || "(not set)",
      display_name: ga4SafeName(displayName, email),
      subscription_active: subscriptionActive === true ? "yes" : "no",
      tier: tier || "(not set)",
    });
    // The identity above only binds to FUTURE events. A member who loads one
    // page and just reads fires nothing else, so their lone page_view stays
    // "(not set)". One explicit event guarantees a named hit per session.
    gtag("event", "user_identified");
  }
  // Do not set user_id for anonymous users (never use "" — GA4 would treat that as one shared id and collapse everyone)
}

function clearGA4UserOnLogout(gtag) {
  if (typeof gtag !== "function") return;
  gtag("set", { user_id: null });
  // null CLEARS a user property; "" is a real value and shows up in reports as
  // its own empty-string bucket alongside "(not set)".
  gtag("set", "user_properties", {
    email: null,
    display_name: null,
    subscription_active: null,
    tier: null,
  });
}

function sendPageView(gtag, location) {
  if (typeof gtag !== "function" || !location) return;
  gtag("event", "page_view", {
    page_title: document.title,
    page_location: window.location.href,
    page_path: (location.pathname || "") + (location.search || "") + (location.hash || ""),
  });
}

class GoogleAnalytics extends React.Component {
  componentDidMount() {
    const gtag = window.gtag;
    const { uid, email, displayName, subscriptionActive, tier } = this.props;
    setGA4User(gtag, uid, email, displayName, subscriptionActive, tier);
    // Do not send page_view here: index.html already sends one so every visitor is counted even if React is slow or gtag loads late
  }

  componentDidUpdate(prevProps) {
    const gtag = window.gtag;
    const { location, history, uid, email, displayName, subscriptionActive, tier } = this.props;

    // Update GA4 user when auth changes (login/logout or subscription update)
    if (
      prevProps.uid !== uid ||
      prevProps.email !== email ||
      prevProps.displayName !== displayName ||
      prevProps.subscriptionActive !== subscriptionActive ||
      prevProps.tier !== tier
    ) {
      if (uid) {
        setGA4User(gtag, uid, email, displayName, subscriptionActive, tier);
      } else if (prevProps.uid) {
        clearGA4UserOnLogout(gtag);
      }
    }

    // Page view on any URL change (navigation, back/forward, query/hash updates)
    const pathChanged = location.pathname !== prevProps.location.pathname;
    const searchChanged = (location.search || "") !== (prevProps.location.search || "");
    const hashChanged = (location.hash || "") !== (prevProps.location.hash || "");
    if (pathChanged || searchChanged || hashChanged) {
      sendPageView(gtag, location);
    }
  }

  render() {
    return null;
  }
}

const mapStateToProps = (state) => ({
  uid: state.auth?.uid ?? "",
  email: state.auth?.email ?? "",
  displayName: state.auth?.displayName ?? "",
  subscriptionActive: state.auth?.subscriptionActive ?? false,
  tier: state.auth?.tier ?? "",
});

export default connect(mapStateToProps)(withRouter(GoogleAnalytics));
